import React, { useState } from 'react';
import { View, TouchableOpacity, TextInput } from 'react-native';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import Modal from '../../../../components/Modal';
import IconButton from '../../../../components/IconButton';
import api from '../../../../utils/api';

const CATEGORY_COLORS = ['#F59E0B', '#3B82F6', '#10B981', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#6B7280'];


export default function CategoryEditModal({ communityId, category, areas, onRequestClose, onSaved }: any) {
  const isEditing = !!category;

  const [name, setName] = useState(category?.name ?? '');
  const [color, setColor] = useState(category?.color ?? CATEGORY_COLORS[0]);
  const [defaultAreaUuid, setDefaultAreaUuid] = useState<string | null>(
    category?.defaultArea?.uuid ?? category?.default_area?.uuid ?? null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSave = async () => {
    if (!name.trim()) return;
    setIsSubmitting(true);
    try {
      const payload = { name: name.trim(), color, default_area_uuid: defaultAreaUuid };
      if (isEditing) {
        await api.put(`/communities/${communityId}/ticket-categories/${category.uuid}`, payload);
      } else {
        await api.post(`/communities/${communityId}/ticket-categories`, payload);
      }
      onSaved();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? 'Error al guardar');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onDelete = async () => {
    if (!confirm(`¿Eliminar la categoría "${category.name}"? Los reportes que ya la tienen la conservan.`)) return;
    setIsSubmitting(true);
    try {
      await api.delete(`/communities/${communityId}/ticket-categories/${category.uuid}`);
      onSaved();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? 'Error al eliminar');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal onRequestClose={onRequestClose} style={{ modal: tw`w-[480px] max-w-[90%]` }}>
      <View>
        <Text style={tw`text-lg font-600 text-secondary mb-4`}>{isEditing ? 'Editar categoría' : 'Nueva categoría'}</Text>

        <Text style={tw`text-gray-500 mb-2`}>Nombre</Text>
        <TextInput
          value={name}
          onChangeText={setName}
          placeholder="Ej. Alumbrado"
          placeholderTextColor={tw.color('gray-400')}
          style={tw`bg-gray-100 border border-gray-200 px-4 h-12 rounded-lg mb-4 text-base`}
        />

        <Text style={tw`text-gray-500 mb-2`}>Color</Text>
        <View style={tw`flex-row flex-wrap gap-2 mb-4`}>
          {CATEGORY_COLORS.map(c => (
            <TouchableOpacity
              key={c}
              onPress={() => setColor(c)}
              style={tw.style(
                `w-8 h-8 rounded-full items-center justify-center`,
                color === c && 'border-2 border-gray-700',
                { backgroundColor: c },
              )}
            />
          ))}
        </View>

        <Text style={tw`text-gray-500`}>Área por default</Text>
        <Text style={tw`text-xs text-gray-400 mb-2`}>
          Al elegir esta categoría, el reporte se asigna a esta área si todavía no tiene una.
        </Text>
        <View style={tw`flex-row flex-wrap gap-2 mb-4`}>
          <AreaOption label="Ninguna" active={!defaultAreaUuid} onPress={() => setDefaultAreaUuid(null)} />
          {areas.map((a: any) => (
            <AreaOption
              key={a.uuid}
              label={a.name}
              active={defaultAreaUuid === a.uuid}
              onPress={() => setDefaultAreaUuid(a.uuid)}
            />
          ))}
        </View>

        <View style={tw`flex-row items-center justify-between mt-2`}>
          {isEditing ? (
            <TouchableOpacity onPress={onDelete} disabled={isSubmitting}>
              <Text style={tw`text-red text-sm`}>Eliminar</Text>
            </TouchableOpacity>
          ) : <View />}

          <IconButton
            disabled={isSubmitting || !name.trim()}
            title={isEditing ? 'Guardar' : 'Crear'}
            onPress={onSave}
          />
        </View>
      </View>
    </Modal>
  );
}

function AreaOption({ label, active, onPress }: any) {
  return (
    <TouchableOpacity
      onPress={onPress}
      style={tw.style(`px-3 py-1.5 rounded-full border`, active ? 'border-transparent bg-secondary' : 'border-gray-200 bg-white')}
    >
      <Text style={tw.style(`text-xs font-500`, active ? 'text-white' : 'text-gray-700')}>{label}</Text>
    </TouchableOpacity>
  );
}
